import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import NavBar from "../../components/common/NavBar";
import CampaignForm from "../../components/sponsor/CampaignForm";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const GET_CAMPAIGN_AND_SPONSORS = gql`
    query GetCampaignAndSponsors($id: ID!) {
        adCampaign(id: $id) {
            id
            name
            campaignType
            status
            marketplacePromotionIncluded
            startsAt
            endsAt
            countries
            targeting {
                countries
                platforms
                sports
            }
            sponsor {
                id
                name
                isActive
                billingCurrency
            }
            zones {
                key
            }
            targetOrganizations {
                id
                name
            }
            creatives {
                id
                adType
                title
                description
                imageUrl
                ctaLabel
                ctaUrl
                weight
                isActive
            }
        }

        sponsors(page: 1, pageSize: 100) {
            objects {
                id
                name
                isActive
                billingCurrency
            }
        }
    }
`;

export default function EditCampaign() {
    const { t } = useTranslation();
    const { id: rawId } = useParams();
    const id = decodeURIComponent(rawId || "");
    const navigate = useNavigate();

    const { data, loading, error } = useQuery(GET_CAMPAIGN_AND_SPONSORS, {
        variables: { id },
        fetchPolicy: "cache-and-network",
    });

    const navBarElem = {
        pageTitle: t('campaigns.editCampaign'),
        links: [
            { pageTitle: t('campaigns.advertising'), navLink: null, active: false },
            { pageTitle: t('campaigns.campaigns'), navLink: "/advertise/campaign", active: false },
            { pageTitle: t('campaigns.editCampaign'), navLink: null, active: true },
        ],
    };

    if (loading) {
        return (
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />
                <section className="content">
                    <div>{t('campaigns.loading')}</div>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />
                <section className="content">
                    <div className="text-danger">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div>
                </section>
            </div>
        );
    }

    const campaign = data?.adCampaign;
    const sponsors = data?.sponsors?.objects ?? [];

    if (!campaign) {
        return (
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />
                <section className="content">
                    <div>{t('campaigns.notFound')}</div>
                </section>
            </div>
        );
    }

    const firstCreative = campaign.creatives?.[0] || null;

    const initialData = {
        campaign: {
            id: campaign.id,
            name: campaign.name || "",
            campaignType: campaign.campaignType || "paid_global",
            status: campaign.status || "draft",
            marketplacePromotionIncluded: !!campaign.marketplacePromotionIncluded,
            sponsorId: campaign.sponsor?.id || "",
            billingCurrency: campaign.sponsor?.billingCurrency || "USD",
            zoneKeys: (campaign.zones || []).map((z) => z.key),
            countries:
                (campaign.countries && campaign.countries.length
                    ? campaign.countries
                    : campaign.targeting?.countries) || ["US", "CA"],
            targetOrganizationIds: (campaign.targetOrganizations || []).map((o) => o.id),
            targeting: {
                countries: campaign.targeting?.countries || ["US", "CA"],
                platforms: campaign.targeting?.platforms || ["ios", "android"],
                sports: campaign.targeting?.sports || [],
            },
            startDate: campaign.startsAt ? campaign.startsAt.slice(0, 10) : "",
            endDate: campaign.endsAt ? campaign.endsAt.slice(0, 10) : "",
        },
        creative: firstCreative
            ? {
                id: firstCreative.id,
                adType: firstCreative.adType || "card",
                title: firstCreative.title || "",
                description: firstCreative.description || "",
                imageUrl: firstCreative.imageUrl || "",
                ctaLabel: firstCreative.ctaLabel || "",
                ctaUrl: firstCreative.ctaUrl || "",
                weight: firstCreative.weight ?? 1,
                isActive: !!firstCreative.isActive,
            }
            : null,
    };

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem} />
            <CampaignForm
                mode="edit"
                initialData={initialData}
                sponsorsFromParent={sponsors}
                organizationsFromParent={campaign.targetOrganizations || []}
                onSaved={() => navigate("/advertise/campaign")}
            />
        </div>
    );
}
